import { z } from "zod";
import { getAccounts } from "./queries";

export const RequestHistoryType = {
  lineManager: "Line Manager",
  financeManager: "Finance approver",
  contactEmail: "Contact email",
  contactMobile: "Contact mobile",
};

export const isNullOrEmpty = (value) =>
  value === "" ||
  value === undefined ||
  value === " " ||
  value?.length === 0 ||
  value === null;

const reminderReceivers = {
  "Form requested": "employee",
  "Send back by requestor": "employee",
  "Reopen": "employee",
  "Form submitted": "requestor",
  "Form resubmitted": "requestor",
  "Validated by requestor": "line manager",
  "Approved by line manager": "finance approver",
};

export const reminderReceiver = (status) => reminderReceivers[status];

export const bankIdentifiers = [
  { value: "IBAN", label: "IBAN" },
  { value: "BIC/SWIFT", label: "BIC/SWIFT" },
  { value: "ACCOUNT AND REG NO", label: "ACCOUNT AND REGISTRATION NUMBER" },
  { value: "FIK", label: "FIK" },
];

const existingAccountSchema = z.object({
  id: z.string().nullable(),
  statusCode: z.string().nullable(),
  accountNumber: z.string().nullable(),
  token: z.string().nullable(),
  name: z.string().nullable(),
  shortName: z.string().nullable(),
  myhrid: z.string().nullable(),
  countryCode: z.string().nullable(),
  label: z.string().nullable(),
  value: z.string().nullable(),
});

export const schema = z.object({
  additionalEmail: z.string(),
  vendorPassCodeByEmail: z.boolean(),
  sendFormLinkTo: z.enum(["personal", "business"]),
  employeeEmail: z
    .union([
      z.literal(""),
      z.string().email("Enter a valid business e-mail (e.g. name@hafnia.com)."),
    ])
    .optional(),
  employeePrivateEmail: z
    .union([
      z.literal(""),
      z.string().email("Enter a valid personal e-mail address."),
    ])
    .optional(),
  employeeFirstName: z
    .string()
    .min(1, "Enter the employee's first name."),
  employeeLastName: z
    .string()
    .min(1, "Enter the employee's last name."),
  employeeAddressLine1: z.string().optional(),
  employeeAddressLine2: z.string().optional(),
  employeeCity: z.string().optional(),
  employeeZipCode: z.string().optional(),
  employeeCountry: z.string().optional(),
  mobilePhone: z
    .string()
    .optional()
    .refine((v) => !v || v.length >= 8, {
      message: "Mobile number looks too short — include country code and full number.",
    }),
  mobileCountryCallingCode: z.string().optional(),
  businessPhone: z
    .string()
    .optional()
    .refine((v) => !v || v.length >= 8, {
      message: "Business number looks too short — include country code and full number.",
    }),
  lineManager: z.string().min(1, "Select a line manager to be notified."),
  financeApprover: z.string().min(1, "Select a finance approver to be notified."),
  selectedAccount: existingAccountSchema.nullable(),
  // bankIdentifier: z.string().min(1, "Select a bank identifier."),
  country: z.string().min(1, "Select a country."),
  currencyId: z
    .number({
      required_error: "Select a currency.",
      invalid_type_error: "Select a currency.",
    })
    .int()
    .positive("Select a currency."),
  file: z
    .instanceof(File)
    .optional()
    .refine(
      (file) =>
        !file ||
        ["image/jpeg", "image/png", "image/jpg", "application/pdf"].includes(
          file.type
        ),
      { message: "Attach a JPG, PNG or PDF file." }
    )
    .refine((file) => !file || file.size <= 10 * 1024 * 1024, {
      message: "File is too large — maximum size is 10 MB.",
    }),
}).superRefine((data, ctx) => {
  const isExistingEmployee = !!data.selectedAccount?.myhrid;

  // Existing employee: business e-mail is mandatory.
  if ((isExistingEmployee || data.sendFormLinkTo === "business") && !data.employeeEmail) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: isExistingEmployee
        ? "Enter the business e-mail."
        : "Enter a business e-mail address to send the form link to.",
      path: ["employeeEmail"],
    });
  }

  // New employee: private e-mail is mandatory.
  if ((!isExistingEmployee || data.sendFormLinkTo === "personal") && !data.employeePrivateEmail) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: isExistingEmployee
        ? "Enter a personal e-mail address to send the form link to."
        : "Enter the personal e-mail.",
      path: ["employeePrivateEmail"],
    });
  }
});

export const requestPortalForm = {
  employeeEmail: "",
  employeePrivateEmail: "",
  sendFormLinkTo: "personal",
  employeeFirstName: "",
  employeeLastName: "",
  mobilePhone: "",
  mobileCountryCallingCode: "",
  businessPhone: "",
  additionalEmail: "",
  lineManager: "",
  financeApprover: "",
  vendorPassCodeByEmail: false,
  country: "",
  currencyId: null,
  // bankIdentifier: "",
  selectedAccount: null,
  employeeAddressLine1: "",
  employeeAddressLine2: "",
  employeeCity: "",
  employeeZipCode: "",
  employeeCountry: "",
};

export const submitData = (data) => {
  return {
    employeeEmail: data.employeeEmail,
    employeePrivateEmail: data.employeePrivateEmail,
    sendFormLinkTo: data.sendFormLinkTo,
    vendorPassCodeByEmail: data.vendorPassCodeByEmail,
    employeeFirstName: data.employeeFirstName,
    employeeLastName: data.employeeLastName,
    mobilePhone:
      data.mobilePhone === "" ||
      data.mobilePhone === undefined ||
      data.mobilePhone === null
        ? null
        : `+${data.mobilePhone.toString().replace(/^\+/, "")}`,
    mobileCountryCallingCode:
      data.mobileCountryCallingCode === "" ||
      data.mobileCountryCallingCode === undefined ||
      data.mobileCountryCallingCode === null
        ? null
        : parseInt(data.mobileCountryCallingCode, 10),
        businessPhone:
      data.businessPhone === "" ||
      data.businessPhone === undefined ||
      data.businessPhone === null
        ? null
        : `+${data.businessPhone.toString().replace(/^\+/, "")}`,
    additionalEmail: data.additionalEmail,
    financeApprover: data.financeApprover,
    lineManager: data.lineManager,
    accountName: data.selectedAccount ? data.selectedAccount.label : null,
    myhrid: data.selectedAccount ? data.selectedAccount.myhrid : null,
    accountNumber: data.selectedAccount
      ? data.selectedAccount.accountNumber
      : null,
    token: data.selectedAccount ? data.selectedAccount.token : null,
    country: data.country,
    currencyId: data.currencyId,
    employeeAddressLine1: data.employeeAddressLine1 || "",
    employeeAddressLine2: data.employeeAddressLine2 || "",
    employeeCity: data.employeeCity || "",
    employeeZipCode: data.employeeZipCode || "",
    // bankIdentifier: data.bankIdentifier,
  };
};

// export const loadAccounts = async (inputValue) => {
//   var accounts = await getAccounts(inputValue);
//   accounts = accounts.data.map((account) => ({
//     ...account,
//     label: `${account.name} | ${account.accountNumber} | ${account.shortName} | ${account.countryCode}`,
//     value: account.id,
//   }));

//   return accounts;
// };
